/**
 * Geschäftspartner — das Detail (AP-31, Phase E, Variante B).
 *
 * **Ein Blatt mit Abschnitten**, nicht sechs Reiter. Die Entscheidung des
 * Auftraggebers vom 12.9.2026, und sie trägt aus zwei Gründen:
 *
 * 1. **Die Reihenfolge folgt der Häufigkeit.** „Wer ist das, wen rufe ich an,
 *    wo liefern wir hin" schlagen alle nach, täglich; Zahlungsziel und
 *    Kontonummern schaut jemand nach, wenn ein Beleg entsteht.
 * 2. **Die beiden gesperrten Abschnitte liegen zusammenhängend.** Die Feldsicht
 *    schneidet damit einen Block am Stück heraus, statt zwei Löcher in die
 *    Mitte zu reißen.
 *
 * Und der Grund, der die Wahl entschieden hat: **Gespeichert wird je
 * Abschnitt.** Ein Abschnitt, den der Benutzer nicht sieht, kann nichts senden
 * — die erste der drei Sicherungen aus AP-31 6.3. Bei einem Formular für den
 * ganzen Partner hinge sie allein am Backend.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/axios';
import { bestaetige } from '@/lib/dialoge';
import {
  FeldStatus,
  FeldStatusZeichen,
  Feldgruppe,
  feldKlasse,
  mitStand,
  useAenderung,
  useFeld,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import { useParams, useNavigate } from 'react-router-dom';
import { Eingaenge } from '@/components/eingang/Eingaenge';
import { useTranslation } from 'react-i18next';
import { erfuelltRecht, COUNTRIES, CURRENCIES } from '@symphenia/shared-types';
import { clientsApi } from '../api/clients.api';
import { fehlertext } from '../api/fehler';
import { OrtVorschlagFeld } from '../components/OrtVorschlagFeld';
import { useAuthStore } from '@/lib/auth.store';
import { useAssistentDatensatz } from '@/lib/assistent/assistent.store';
import { formatiereTelefon } from '@/lib/telefon';
import { normalisiereWebsite } from '@/lib/website';
import type {
  ClientDetail,
  Address,
  AddressType,
  ContactPerson,
  FormOfAddress,
  Salutation,
  CreateAddressPayload,
  CreateContactPayload,
  Ortsvorschlag,
} from '../types/client.types';

const ABSCHNITTE = ['base', 'contacts', 'addresses', 'payment', 'accounting', 'projects'] as const;
type Abschnitt = (typeof ABSCHNITTE)[number];

const LEERE_ADRESSE: CreateAddressPayload = {
  type: 'MAIN',
  label: '',
  recipient: '',
  street: '',
  addition: '',
  city: '',
  zip: '',
  country: 'AT',
  isDefault: false,
};

const LEERER_KONTAKT: CreateContactPayload = {
  salutation: '',
  formOfAddress: 'FORMAL',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneExtension: '',
  position: '',
  isDefault: false,
};

/** Die Adressarten, die jeder Beleg kennt — der Rest heißt `OTHER` mit Bezeichnung. */
const FESTE_ADRESSARTEN = ['MAIN', 'BILLING', 'DELIVERY'] as const;

/**
 * Das Land, in dem eine Telefonnummer ohne Landesvorwahl gemeint ist: das der
 * Standardadresse, sonst Österreich.
 */
function landDesPartners(client: ClientDetail): string {
  return (client.addresses.find((a) => a.isDefault) ?? client.addresses[0])?.country ?? 'AT';
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuthStore();

  const darf = useCallback(
    (permission: string) => erfuelltRecht(permission, currentUser?.permissions),
    [currentUser?.permissions],
  );
  const canManage = darf('crm.client.manage');
  const canDelete = darf('crm.client.delete');
  const sichtZahlung = darf('crm.client.payment.view');
  const sichtBuchhaltung = darf('crm.client.accounting.view');
  const sichtAuftraege = darf('projects.project.read');

  const [client, setClient] = useState<ClientDetail | null>(null);
  // AP-18: Der Assistent weiß, welcher Partner geöffnet ist („dieser Kunde").
  useAssistentDatensatz(client ? { art: 'client', id: client.id, bezeichnung: t('assistant.record.client', { name: client.name }) } : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hinweise, setHinweise] = useState<string[]>([]);
  const [adressBezeichnungen, setAdressBezeichnungen] = useState<string[]>([]);
  const [standardAnsprache, setStandardAnsprache] = useState<FormOfAddress>('FORMAL');

  const laden = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await clientsApi.get(id);
      setClient(data);
      setError(null);
    } catch {
      setError(t('clients.notFound'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Die eigenen Adressarten des Mandanten. Fehlen sie, bleiben die drei festen
  // und die freie Eingabe — die Maske ist ohne sie vollständig.
  useEffect(() => {
    clientsApi
      .options()
      .then(({ data }) => {
        setAdressBezeichnungen(data.addressLabels);
        setStandardAnsprache(data.contactFormOfAddress ?? 'FORMAL');
      })
      .catch(() => setAdressBezeichnungen([]));
  }, []);

  /**
   * **Eine Speicherstelle für den Partner** (AP-36 Phase E). Jedes Feld schickt
   * nur sich selbst — was ein Abschnitt nicht kennt, kann er nicht leeren
   * (AP-31 6.3 a). Danach wird neu geladen: Der Server setzt den Namen einer
   * Person zusammen, vergibt die Lieferantennummer und reicht die Vorgaben
   * einer neu gesetzten Rolle nach.
   */
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `client:${id}`,
    bezeichnung: client?.name,
    daten: client as unknown as Datensatz | null,
    schreibe: async (patch, stand) => {
      const { data } = await api.patch<Datensatz>(`/clients/${id}`, patch, mitStand(stand));
      setHinweise((data.warnings as string[] | undefined) ?? []);
      setError(null);
      await laden();
      return data;
    },
    keepalive: { url: `/clients/${id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  if (loading && !client) {
    return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (!client) {
    return <div className="p-6 text-sm text-destructive">{error ?? t('clients.notFound')}</div>;
  }

  const istPerson = client.type === 'INDIVIDUAL';

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <Kopf
        client={client}
        stelle={stelle}
        canManage={canManage}
        canDelete={canDelete}
        onZurueck={() => navigate('/clients')}
        onGeloescht={() => navigate('/clients')}
        onFehler={setError}
      />

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {hinweise.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
          <ul className="space-y-1">
            {hinweise.map((h) => (
              <li key={h} className="text-foreground">
                {t(h.replace('clients.warn.', 'clients.warnings.'))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_14rem]">
        <div className="space-y-6">
          <StammdatenAbschnitt client={client} stelle={stelle} canManage={canManage} />

          <AnsprechpartnerAbschnitt
            client={client}
            canManage={canManage}
            istPerson={istPerson}
            standardAnsprache={standardAnsprache}
            onGeaendert={laden}
          />

          <AdressenAbschnitt
            client={client}
            canManage={canManage}
            adressBezeichnungen={adressBezeichnungen}
            onGeaendert={laden}
          />

          {sichtZahlung ? (
            // Neu aufgebaut, wenn eine Rolle dazukommt: Der Server hat dann die
            // Vorgaben des Mandanten eingetragen, und die Maske soll sie zeigen.
            <ZahlungAbschnitt
              key={`${client.isCustomer}-${client.isSupplier}`}
              client={client}
              stelle={stelle}
              canManage={canManage}
              istPerson={istPerson}
            />
          ) : (
            <GesperrterAbschnitt id="payment" titel={t('clients.sections.payment')} />
          )}

          {sichtBuchhaltung ? (
            <BuchhaltungAbschnitt stelle={stelle} canManage={canManage} lieferant={client.isSupplier} />
          ) : (
            <GesperrterAbschnitt id="accounting" titel={t('clients.sections.accounting')} />
          )}

          {sichtAuftraege && <AuftraegeAbschnitt client={client} />}

          {/* AP-39 9.6: Dokumente aus dem Posteingang, am Partner abgelegt */}
          <Eingaenge targetKind="PARTNER" targetId={client.id} />
        </div>

        <Ankerleiste
          client={client}
          sichtbar={{
            payment: true,
            accounting: true,
            projects: sichtAuftraege,
          }}
        />
      </div>
    </div>
  );
}

// ─── Kopf ─────────────────────────────────────────────────────────────────────

function Kopf({
  client,
  stelle,
  canManage,
  canDelete,
  onZurueck,
  onGeloescht,
  onFehler,
}: {
  client: ClientDetail;
  stelle: Speicherstelle<unknown>;
  canManage: boolean;
  canDelete: boolean;
  onZurueck: () => void;
  onGeloescht: () => void;
  onFehler: (text: string) => void;
}) {
  const { t } = useTranslation();
  // Deaktivieren ist eine Handlung, kein Feld — mit dem Stand des Partners.
  const aktiv = useAenderung(stelle, `client:${client.id}:isActive`);
  const [loeschDialog, setLoeschDialog] = useState(false);
  const [tippprobe, setTippprobe] = useState('');

  // AP-31, Entscheidung 16: Tippbestätigung nur bei Partnern mit Verlauf.
  const hatVerlauf = client.stats.projectCount > 0 || client.stats.totalTimeEntries > 0;
  const darfLoeschen = !hatVerlauf || tippprobe.trim() === client.name;

  const loeschen = async () => {
    try {
      await clientsApi.purge(client.id);
      onGeloescht();
    } catch (err: unknown) {
      const antwort = (err as { response?: { status?: number; data?: { message?: unknown } } })
        ?.response;
      const inhalt = antwort?.data?.message;
      const daten = antwort?.data as { articleCount?: number } | undefined;
      if (antwort?.status === 409 && (inhalt === 'clients.deleteHasArticles' || daten?.articleCount)) {
        // AP-28: Artikel, an denen der Partner als Lieferant steht.
        onFehler(t('clients.delete.hasArticles', { count: daten?.articleCount ?? 0 }));
      } else if (antwort?.status === 409) {
        const anzahl =
          typeof inhalt === 'object' && inhalt !== null && 'projectCount' in inhalt
            ? (inhalt as { projectCount: number }).projectCount
            : ((antwort?.data as { projectCount?: number } | undefined)?.projectCount ?? 0);
        onFehler(t('clients.delete.hasProjects', { count: anzahl }));
      } else {
        onFehler(t('common.error'));
      }
      setLoeschDialog(false);
    }
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onZurueck}
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        ‹ {t('clients.title')}
      </button>

      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{client.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono">{client.clientNumber}</span>
            {client.supplierNumber && <span className="font-mono">{client.supplierNumber}</span>}
            {client.isCustomer && <Abzeichen text={t('clients.role.customer')} />}
            {client.isSupplier && <Abzeichen text={t('clients.role.supplier')} />}
            {!client.isActive && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {t('common.inactive')}
              </span>
            )}
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void aktiv.sende({ felder: ['isActive'], patch: { isActive: !client.isActive }, wert: 'isActive' })}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
            >
              {client.isActive ? t('clients.delete.deactivate') : t('clients.delete.activate')}
            </button>
            <FeldStatusZeichen status={aktiv.status} />
            {canDelete && (
              <button
                type="button"
                onClick={() => setLoeschDialog(true)}
                className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
              >
                {t('clients.delete.permanent')}
              </button>
            )}
          </div>
        )}
      </div>

      {loeschDialog && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
          <p className="font-medium text-foreground">{t('clients.delete.confirmTitle')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('clients.delete.confirmText')}</p>
          {hatVerlauf && (
            <div className="mt-2">
              <label className="mb-1 block text-sm text-foreground">
                {t('clients.delete.typeName')}
              </label>
              <input
                type="text"
                value={tippprobe}
                onChange={(e) => setTippprobe(e.target.value)}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
              />
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setLoeschDialog(false);
                setTippprobe('');
              }}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={!darfLoeschen}
              onClick={() => void loeschen()}
              className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40"
            >
              {t('clients.delete.permanent')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Abzeichen({ text }: { text: string }) {
  return (
    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      {text}
    </span>
  );
}

// ─── Ankerleiste mit Belegreife-Ampel ────────────────────────────────────────

function Ankerleiste({
  client,
  sichtbar,
}: {
  client: ClientDetail;
  sichtbar: Record<string, boolean>;
}) {
  const { t } = useTranslation();
  const { readiness } = client;

  return (
    <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      {/*
        Die Ampel rechnet der **Server** (AP-31 8.3): Würde die Maske rechnen,
        stünde bei einem Monteur „UID fehlt", obwohl sie erfasst ist — er
        bekommt das Feld wegen der Feldsicht gar nicht zu sehen.
      */}
      <div
        className={`rounded-lg border px-4 py-3 text-sm ${
          readiness.ok
            ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/40'
            : 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
        }`}
      >
        <p className="font-medium text-foreground">
          {readiness.ok ? t('clients.readiness.ok') : t('clients.readiness.incomplete')}
        </p>
        {readiness.missing.length > 0 && (
          <>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('clients.readiness.missingLabel')}
            </p>
            <ul className="mt-0.5 list-disc pl-4 text-xs text-foreground">
              {readiness.missing.map((feld) => (
                <li key={feld}>{t(`clients.readiness.fields.${feld}`, { defaultValue: feld })}</li>
              ))}
            </ul>
          </>
        )}
        {readiness.hints.length > 0 && (
          <>
            <p className="mt-2 text-xs text-muted-foreground">{t('clients.readiness.hintLabel')}</p>
            <ul className="mt-0.5 list-disc pl-4 text-xs text-muted-foreground">
              {readiness.hints.map((feld) => (
                <li key={feld}>{t(`clients.readiness.fields.${feld}`, { defaultValue: feld })}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      <nav className="rounded-lg border border-border bg-card px-2 py-2 text-sm">
        {ABSCHNITTE.filter((a) => sichtbar[a] !== false).map((a) => (
          <a
            key={a}
            href={`#${a}`}
            className="block rounded px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t(`clients.sections.${a}`)}
          </a>
        ))}
      </nav>
    </aside>
  );
}

// ─── Abschnitts-Gerüst ────────────────────────────────────────────────────────

function Abschnittskarte({
  id,
  titel,
  aktion,
  children,
}: {
  id: Abschnitt;
  titel: string;
  aktion?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <h2 className="font-semibold text-foreground">{titel}</h2>
        {aktion}
      </div>
      <div className="px-4 py-4">{children}</div>
    </section>
  );
}

/**
 * An der Stelle eines gesperrten Abschnitts steht ein Hinweis, **dass es ihn
 * gibt** — kein Loch. Wer nicht weiß, dass ein Abschnitt existiert, fragt auch
 * nicht nach dem Recht dafür.
 */
function GesperrterAbschnitt({ id, titel }: { id: Abschnitt; titel: string }) {
  const { t } = useTranslation();
  return (
    <section id={id} className="rounded-lg border border-dashed border-border px-4 py-3">
      <h2 className="font-semibold text-muted-foreground">🔒 {titel}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('clients.sections.locked')}</p>
    </section>
  );
}

function Feld({
  label,
  hinweis,
  children,
}: {
  label: string;
  hinweis?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-foreground">{label}</label>
      {children}
      {hinweis && <p className="mt-1 text-xs text-muted-foreground">{hinweis}</p>}
    </div>
  );
}

function Fehlerzeile({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{text}</div>;
}

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

/**
 * Der Name steht größer und fett — `text-xl` ist gegenüber dem bisherigen
 * `text-lg` rund 2 pt mehr (AP-31 Nachtrag).
 *
 * Er ist die **eine Belegzeile** (AP-31 4.4) — das Feld, unter dem der Partner
 * in jeder Liste, jedem Auswahlfeld und auf jedem Beleg auftaucht. Zwischen
 * fünfzehn gleich aussehenden Eingabefeldern verschwindet es sonst neben der
 * Website.
 */
const nameEingabe = `${eingabe} text-xl font-bold`;

function AbbrechenKnopf({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
    >
      {t('common.cancel')}
    </button>
  );
}

// ─── Stammdaten ───────────────────────────────────────────────────────────────

function StammdatenAbschnitt({
  client,
  stelle,
  canManage,
}: {
  client: ClientDetail;
  stelle: Speicherstelle<unknown>;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const istPerson = client.type === 'INDIVIDUAL';
  const land = landDesPartners(client);
  const gesperrt = !canManage;

  const name = useFeld(stelle, 'name', { label: t('clients.companyName'), pflicht: true, gesperrt });
  const salutation = useFeld(stelle, 'salutation', { art: 'auswahl', label: t('clients.person.salutation'), gesperrt });
  const title = useFeld(stelle, 'title', { label: t('clients.person.title'), gesperrt });
  const firstName = useFeld(stelle, 'firstName', { label: t('clients.person.firstName'), gesperrt });
  const lastName = useFeld(stelle, 'lastName', { label: t('clients.person.lastName'), pflicht: istPerson, gesperrt });
  const email = useFeld(stelle, 'email', { label: t('clients.email'), gesperrt });
  // Telefon und Website werden beim Verlassen in ihre Schreibweise gebracht —
  // dieselbe Regel wie bisher, nur ohne Zwischenschritt im Formular.
  const phone = useFeld(stelle, 'phone', {
    label: t('clients.phone'),
    gesperrt,
    lies: (x) => formatiereTelefon(x, land) || null,
  });
  const website = useFeld(stelle, 'website', {
    label: t('clients.website'),
    gesperrt,
    lies: (x) => normalisiereWebsite(x) || null,
  });
  const notes = useFeld(stelle, 'notes', { label: t('clients.notes'), gesperrt });
  // AP-44 6.1: Lesehinweise für die KI-Lesung — nur am Lieferanten und nur mit dem Baustein. Kein Feldrecht:
  // Wer den Partner sieht, sieht sie; deshalb stehen sie hier und nicht im Abschnitt Buchhaltung.
  const kiLesung = useAuthStore((s) => s.hasModule)('INCOMING_INVOICES.AI_CAPTURE') && client.isSupplier;
  const lesehinweise = useFeld(stelle, 'aiReadingNotes', { label: t('clients.aiReadingNotes'), gesperrt });

  return (
    <Abschnittskarte id="base" titel={t('clients.sections.base')}>
      <div className="space-y-4">
        {/* Rollen & Nummern — Identität, kein eigener Abschnitt (AP-31 8.2) */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className="mb-1 block text-sm font-medium text-foreground">
              {t('clients.role.label')}
            </span>
            {/* Eine Rolle muss bleiben: Beide zusammen prüft der Server, also bilden sie eine Gruppe. */}
            <Feldgruppe
              stelle={stelle}
              label={t('clients.role.label')}
              pruefe={(w) => (!w.isCustomer && !w.isSupplier ? t('clients.roleRequired') : null)}
            >
              <div className="flex items-center gap-4">
                <Rolle stelle={stelle} feld="isCustomer" label={t('clients.role.isCustomer')} gesperrt={gesperrt} />
                <Rolle stelle={stelle} feld="isSupplier" label={t('clients.role.isSupplier')} gesperrt={gesperrt} />
              </div>
            </Feldgruppe>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Feld label={t('clients.clientNumber')}>
              <div className="px-1 py-2 font-mono text-sm text-muted-foreground">
                {client.clientNumber}
              </div>
            </Feld>
            <Feld label={t('clients.supplierNumber')}>
              <div className="px-1 py-2 font-mono text-sm text-muted-foreground">
                {client.supplierNumber ?? '—'}
              </div>
            </Feld>
          </div>
        </div>

        {istPerson ? (
          <div className="grid gap-4 sm:grid-cols-4">
            <Feld label={t('clients.person.salutation')}>
              <select className={feldKlasse} {...salutation.props}>
                <option value="">—</option>
                <option value="MR">{t('clients.salutations.MR')}</option>
                <option value="MRS">{t('clients.salutations.MRS')}</option>
                <option value="FAMILY">{t('clients.salutations.FAMILY')}</option>
                <option value="NONE">{t('clients.salutations.NONE')}</option>
              </select>
              <FeldStatus status={salutation} />
            </Feld>
            <Feld label={t('clients.person.title')}>
              <input type="text" className={feldKlasse} {...title.props} />
              <FeldStatus status={title} />
            </Feld>
            <Feld label={t('clients.person.firstName')}>
              <input type="text" className={nameEingabe} {...firstName.props} />
              <FeldStatus status={firstName} />
            </Feld>
            <Feld label={t('clients.person.lastName')}>
              <input type="text" className={nameEingabe} {...lastName.props} />
              <FeldStatus status={lastName} />
            </Feld>
          </div>
        ) : (
          <Feld label={t('clients.companyName')}>
            <input type="text" className={nameEingabe} {...name.props} />
            <FeldStatus status={name} />
          </Feld>
        )}

        {istPerson && (
          <p className="text-xs text-muted-foreground">
            {t('clients.person.derivedName')}{' '}
            <span className="text-xl font-bold text-foreground">{client.name}</span>
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Feld label={t('clients.email')}>
            <input type="email" className={feldKlasse} {...email.props} />
            <FeldStatus status={email} />
          </Feld>
          <Feld label={t('clients.phone')}>
            <input type="tel" placeholder={t('clients.phonePlaceholder')} className={feldKlasse} {...phone.props} />
            <FeldStatus status={phone} />
          </Feld>
          <Feld label={t('clients.website')}>
            {/* Textfeld statt `type="url"`: Der Browser wiese sonst „www.acme.at"
                ab. Das Protokoll kommt beim Verlassen dazu. */}
            <input type="text" inputMode="url" placeholder="www.beispiel.at" className={feldKlasse} {...website.props} />
            <FeldStatus status={website} />
          </Feld>
        </div>

        <Feld label={t('clients.notes')}>
          <textarea rows={2} className={`${feldKlasse} resize-none`} {...notes.props} />
          <FeldStatus status={notes} />
        </Feld>

        {kiLesung && (
          <Feld label={t('clients.aiReadingNotes')} hinweis={t('clients.aiReadingNotesHint')}>
            <textarea rows={3} maxLength={1000} className={`${feldKlasse} resize-y`} {...lesehinweise.props} />
            <FeldStatus status={lesehinweise} />
          </Feld>
        )}
      </div>
    </Abschnittskarte>
  );
}

/** Eine Rolle des Partners — Kunde oder Lieferant; beide zusammen sind die Gruppe. */
function Rolle({
  stelle,
  feld,
  label,
  gesperrt,
}: {
  stelle: Speicherstelle<unknown>;
  feld: string;
  label: string;
  gesperrt: boolean;
}) {
  const f = useFeld<boolean>(stelle, feld, { art: 'schalter', label, gesperrt });
  return (
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input type="checkbox" {...f.schalter} />
      {label}
      <FeldStatusZeichen status={f} />
    </label>
  );
}

// ─── Ansprechpartner ──────────────────────────────────────────────────────────

function AnsprechpartnerAbschnitt({
  client,
  canManage,
  istPerson,
  standardAnsprache,
  onGeaendert,
}: {
  client: ClientDetail;
  canManage: boolean;
  istPerson: boolean;
  /** Vorgabe des Mandanten für einen neuen Ansprechpartner. */
  standardAnsprache: FormOfAddress;
  onGeaendert: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [neuOffen, setNeuOffen] = useState(false);
  /** Gesetzt = dieser Eintrag ist aufgeklappt und wird feldweise geändert. */
  const [offen, setOffen] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const entfernen = async (k: ContactPerson) => {
    if (!(await bestaetige(t('clients.deleteContactConfirm', { defaultValue: 'Ansprechpartner entfernen?' }), { ok: t('common.delete'), gefaehrlich: true }))) return;
    try {
      await clientsApi.deleteContact(client.id, k.id);
      if (offen === k.id) setOffen(null);
      await onGeaendert();
    } catch (err: unknown) {
      setFehler(fehlertext(err, t));
    }
  };

  /** „Tel. +43 732 123456-23" — die Durchwahl hängt an der Zentrale des Partners. */
  const durchwahlText = (k: ContactPerson) => {
    if (!k.phoneExtension) return null;
    return client.phone
      ? `${t('clients.contactShort.phone')} ${client.phone}-${k.phoneExtension}`
      : `${t('clients.phoneExtension')} ${k.phoneExtension}`;
  };

  return (
    <Abschnittskarte
      id="contacts"
      titel={t('clients.sections.contacts')}
      aktion={
        canManage ? (
          <button
            type="button"
            onClick={() => {
              setFehler(null);
              setNeuOffen(true);
            }}
            className="text-sm font-medium text-primary hover:underline"
          >
            + {t('clients.addContact')}
          </button>
        ) : undefined
      }
    >
      {/* Bei einer Person ein Hinweis statt einer leeren Liste (AP-31 4.4). */}
      {istPerson && client.contactPersons.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('clients.person.contactHint')}</p>
      )}
      {!istPerson && client.contactPersons.length === 0 && !neuOffen && (
        <p className="text-sm text-muted-foreground">{t('clients.noContacts')}</p>
      )}

      <Fehlerzeile text={fehler} />

      {client.contactPersons.length > 0 && (
        <ul className="divide-y divide-border">
          {client.contactPersons.map((k) => (
            <li key={k.id} className={`py-2 text-sm ${offen === k.id ? 'bg-muted/40' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  {/* Anrede, Ansprache und Rolle stehen im aufgeklappten Eintrag, nicht in der Zeile. */}
                  <span className="text-foreground">
                    {k.firstName} <span className="font-bold">{k.lastName}</span>
                  </span>
                  {k.isDefault && (
                    <span className="ml-1 text-xs text-primary">{t('clients.default')}</span>
                  )}
                  <div className="text-muted-foreground">
                    {[
                      k.position,
                      k.email,
                      durchwahlText(k),
                      k.phone ? `${t('clients.contactShort.mobile')} ${k.phone}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setOffen((o) => (o === k.id ? null : k.id))}
                      className="text-xs text-muted-foreground hover:text-primary"
                    >
                      {offen === k.id ? t('common.close', { defaultValue: 'Schließen' }) : t('common.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void entfernen(k)}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>

              {/* Aufgeklappt: die Felder des Eintrags. Sie speichern beim Verlassen (AP-36). */}
              {offen === k.id && canManage && (
                <KontaktFelder client={client} kontakt={k} onGespeichert={onGeaendert} />
              )}
            </li>
          ))}
        </ul>
      )}

      {neuOffen && canManage && (
        <NeuerKontakt
          client={client}
          standardAnsprache={standardAnsprache}
          onFertig={async () => {
            setNeuOffen(false);
            await onGeaendert();
          }}
          onAbbrechen={() => setNeuOffen(false)}
        />
      )}
    </Abschnittskarte>
  );
}

/**
 * Ein Ansprechpartner als **Gruppe** (AP-36 Phase E): Die Felder gehören
 * zusammen und gehen beim Verlassen des Eintrags in einem PATCH hinaus, mit dem
 * Stand dieser Zeile.
 */
function KontaktFelder({
  client,
  kontakt,
  onGespeichert,
}: {
  client: ClientDetail;
  kontakt: ContactPerson;
  onGespeichert: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const land = landDesPartners(client);
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `contactPerson:${kontakt.id}`,
    bezeichnung: [kontakt.firstName, kontakt.lastName].filter(Boolean).join(' '),
    daten: kontakt as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const { data } = await api.patch<Datensatz>(
        `/clients/${client.id}/contacts/${kontakt.id}`,
        patch,
        mitStand(stand),
      );
      // „Standard" nimmt den bisherigen Standard mit — die Liste liest neu.
      await onGespeichert();
      return data;
    },
    keepalive: { url: `/clients/${client.id}/contacts/${kontakt.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  return (
    <Feldgruppe stelle={stelle} label={t('clients.sections.contacts')} className="mt-3 space-y-3 border-t border-border pt-3">
      <KontaktEingaben stelle={stelle} client={client} land={land} />
    </Feldgruppe>
  );
}

function KontaktEingaben({
  stelle,
  client,
  land,
}: {
  stelle: Speicherstelle<unknown>;
  client: ClientDetail;
  land: string;
}) {
  const { t } = useTranslation();
  const salutation = useFeld(stelle, 'salutation', { art: 'auswahl', label: t('clients.person.salutation') });
  const ansprache = useFeld(stelle, 'formOfAddress', { art: 'auswahl', label: t('clients.formOfAddress'), pflicht: true });
  const lastName = useFeld(stelle, 'lastName', { label: t('clients.person.lastName') });
  const firstName = useFeld(stelle, 'firstName', { label: t('clients.person.firstName'), pflicht: true });
  const position = useFeld(stelle, 'position', { label: t('clients.position') });
  const email = useFeld(stelle, 'email', { label: t('clients.email') });
  const phone = useFeld(stelle, 'phone', { label: t('clients.mobilePhone'), lies: (x) => formatiereTelefon(x, land) || null });
  const durchwahl = useFeld(stelle, 'phoneExtension', {
    label: t('clients.phoneExtension'),
    lies: (x) => x.replace(/[^\d-]/g, '') || null,
  });
  const standard = useFeld<boolean>(stelle, 'isDefault', { art: 'schalter', label: t('clients.setAsDefault') });

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t('clients.person.salutation')}>
          <select className={feldKlasse} {...salutation.props}>
            <option value="">—</option>
            <option value="MR">{t('clients.salutations.MR')}</option>
            <option value="MRS">{t('clients.salutations.MRS')}</option>
            <option value="NONE">{t('clients.salutations.NONE')}</option>
          </select>
          <FeldStatus status={salutation} />
        </Feld>
        <Feld label={t('clients.formOfAddress')}>
          {/* Per Sie oder per du — vorbelegt aus den Mandanten-Einstellungen. */}
          <div className="flex h-[38px] items-center gap-4">
            {(['FORMAL', 'INFORMAL'] as const).map((wert) => (
              <label key={wert} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name={`formOfAddress-${client.id}`}
                  checked={ansprache.wert === wert}
                  onChange={() => ansprache.setze(wert)}
                />
                {t(`clients.formsOfAddress.${wert}`)}
              </label>
            ))}
            <FeldStatusZeichen status={ansprache} />
          </div>
          <FeldStatus status={ansprache} zeichen={false} />
        </Feld>
        {/* Nachname zuerst (Wunsch vom 13.9.2026) — er ist, wonach man sucht, und steht fett. */}
        <Feld label={t('clients.person.lastName')}>
          <input type="text" className={`${feldKlasse} font-bold`} {...lastName.props} />
          <FeldStatus status={lastName} />
        </Feld>
        <Feld label={t('clients.person.firstName')}>
          <input type="text" className={feldKlasse} {...firstName.props} />
          <FeldStatus status={firstName} />
        </Feld>
        <Feld label={t('clients.position')}>
          <input type="text" className={feldKlasse} {...position.props} />
          <FeldStatus status={position} />
        </Feld>
        <Feld label={t('clients.email')}>
          <input type="email" className={feldKlasse} {...email.props} />
          <FeldStatus status={email} />
        </Feld>
        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <Feld label={t('clients.mobilePhone')}>
            <input type="tel" placeholder={t('clients.phonePlaceholder')} className={feldKlasse} {...phone.props} />
            <FeldStatus status={phone} />
          </Feld>
          <Feld
            label={t('clients.phoneExtension')}
            hinweis={client.phone ? t('clients.phoneExtensionHint', { phone: client.phone }) : undefined}
          >
            <input type="text" inputMode="numeric" className={feldKlasse} {...durchwahl.props} />
            <FeldStatus status={durchwahl} />
          </Feld>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" {...standard.schalter} />
        {t('clients.setAsDefault')}
        <FeldStatusZeichen status={standard} />
      </label>
      <FeldStatus status={standard} zeichen={false} />
    </>
  );
}

/** Neuer Ansprechpartner: nur zum Anlegen, danach gilt Fall 1 am Eintrag. */
function NeuerKontakt({
  client,
  standardAnsprache,
  onFertig,
  onAbbrechen,
}: {
  client: ClientDetail;
  standardAnsprache: FormOfAddress;
  onFertig: () => Promise<void>;
  onAbbrechen: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateContactPayload>({ ...LEERER_KONTAKT, formOfAddress: standardAnsprache });
  const [fehler, setFehler] = useState<string | null>(null);
  const land = landDesPartners(client);

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await clientsApi.addContact(client.id, {
        ...form,
        phone: formatiereTelefon(form.phone ?? '', land),
        phoneExtension: (form.phoneExtension ?? '').replace(/[^\d-]/g, ''),
      });
      await onFertig();
    } catch (err: unknown) {
      setFehler(fehlertext(err, t));
    }
  };

  // Dieselben Felder in derselben Anordnung wie am Eintrag (KontaktEingaben) —
  // Wunsch vom 27.9.2026: beim Anlegen nichts erst hinterher nachtragen müssen.
  return (
    <form onSubmit={anlegen} className="mt-4 space-y-3 border-t border-border pt-4">
      <p className="text-sm font-medium text-foreground">{t('clients.addContact')}</p>
      <Fehlerzeile text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t('clients.person.salutation')}>
          <select
            value={form.salutation ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, salutation: e.target.value as Salutation | '' }))}
            className={eingabe}
          >
            <option value="">—</option>
            <option value="MR">{t('clients.salutations.MR')}</option>
            <option value="MRS">{t('clients.salutations.MRS')}</option>
            <option value="NONE">{t('clients.salutations.NONE')}</option>
          </select>
        </Feld>
        <Feld label={t('clients.formOfAddress')}>
          {/* Per Sie oder per du — vorbelegt aus den Mandanten-Einstellungen. */}
          <div className="flex h-[38px] items-center gap-4">
            {(['FORMAL', 'INFORMAL'] as const).map((wert) => (
              <label key={wert} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name={`formOfAddress-neu-${client.id}`}
                  checked={form.formOfAddress === wert}
                  onChange={() => setForm((f) => ({ ...f, formOfAddress: wert }))}
                />
                {t(`clients.formsOfAddress.${wert}`)}
              </label>
            ))}
          </div>
        </Feld>
        <Feld label={t('clients.person.lastName')}>
          <input
            type="text"
            value={form.lastName ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            className={`${eingabe} font-bold`}
          />
        </Feld>
        <Feld label={t('clients.person.firstName')}>
          <input
            type="text"
            required
            value={form.firstName}
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            className={eingabe}
          />
        </Feld>
        <Feld label={t('clients.position')}>
          <input
            type="text"
            value={form.position ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
            className={eingabe}
          />
        </Feld>
        <Feld label={t('clients.email')}>
          <input
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={eingabe}
          />
        </Feld>
        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <Feld label={t('clients.mobilePhone')}>
            <input
              type="tel"
              value={form.phone ?? ''}
              placeholder={t('clients.phonePlaceholder')}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className={eingabe}
            />
          </Feld>
          <Feld
            label={t('clients.phoneExtension')}
            hinweis={client.phone ? t('clients.phoneExtensionHint', { phone: client.phone }) : undefined}
          >
            <input
              type="text"
              inputMode="numeric"
              value={form.phoneExtension ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, phoneExtension: e.target.value }))}
              className={eingabe}
            />
          </Feld>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={form.isDefault ?? false}
          onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
        />
        {t('clients.setAsDefault')}
      </label>
      <div className="flex justify-end gap-2">
        <AbbrechenKnopf onClick={onAbbrechen} />
        <button
          type="submit"
          disabled={!form.firstName.trim()}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {t('common.create', { defaultValue: 'Anlegen' })}
        </button>
      </div>
    </form>
  );
}

// ─── Adressen ─────────────────────────────────────────────────────────────────

/** Der Wert der Adressart-Auswahl: eine feste Art, eine Mandanten-Bezeichnung oder frei. */
const FREI = '__frei__';
const BEZEICHNUNG = 'label:';

function AdressenAbschnitt({
  client,
  canManage,
  adressBezeichnungen,
  onGeaendert,
}: {
  client: ClientDetail;
  canManage: boolean;
  adressBezeichnungen: string[];
  onGeaendert: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [neuOffen, setNeuOffen] = useState(false);
  /** Gesetzt = diese Adresse ist aufgeklappt und wird feldweise geändert. */
  const [offen, setOffen] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const artName = (a: { type: AddressType; label?: string | null }) =>
    a.type === 'OTHER' ? a.label || t('clients.addressTypes.OTHER') : t(`clients.addressTypes.${a.type}`);

  const entfernen = async (a: Address) => {
    if (!(await bestaetige(t('clients.deleteAddressConfirm', { defaultValue: 'Adresse entfernen?' }), { ok: t('common.delete'), gefaehrlich: true }))) return;
    try {
      await clientsApi.deleteAddress(client.id, a.id);
      if (offen === a.id) setOffen(null);
      await onGeaendert();
    } catch (err: unknown) {
      setFehler(fehlertext(err, t));
    }
  };

  return (
    <Abschnittskarte
      id="addresses"
      titel={t('clients.sections.addresses')}
      aktion={
        canManage ? (
          <button
            type="button"
            onClick={() => {
              setFehler(null);
              setNeuOffen(true);
            }}
            className="text-sm font-medium text-primary hover:underline"
          >
            + {t('clients.addAddress')}
          </button>
        ) : undefined
      }
    >
      {client.addresses.length === 0 && !neuOffen && (
        <p className="text-sm text-muted-foreground">{t('clients.noAddresses')}</p>
      )}

      <Fehlerzeile text={fehler} />

      {client.addresses.length > 0 && (
        <ul className="divide-y divide-border">
          {client.addresses.map((a) => (
            <li key={a.id} className={`py-2 text-sm ${offen === a.id ? 'bg-muted/40' : ''}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {artName(a)}
                  </span>
                  {a.isDefault && (
                    <span className="ml-1 text-xs text-primary">{t('clients.default')}</span>
                  )}
                  {/* Zusatz unter dem Empfänger, vor der Straße — so steht er auch am Beleg. */}
                  <div className="mt-1 text-foreground">
                    {a.recipient && <div>{a.recipient}</div>}
                    {a.addition && <div>{a.addition}</div>}
                    <div>{a.street}</div>
                    <div>
                      {a.zip} {a.city}, {a.country}
                    </div>
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setOffen((o) => (o === a.id ? null : a.id))}
                      className="text-xs text-muted-foreground hover:text-primary"
                    >
                      {offen === a.id ? t('common.close', { defaultValue: 'Schließen' }) : t('common.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void entfernen(a)}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>

              {/* Aufgeklappt: die Felder der Adresse. Sie speichern beim Verlassen (AP-36). */}
              {offen === a.id && canManage && (
                <AdressFelder
                  client={client}
                  adresse={a}
                  adressBezeichnungen={adressBezeichnungen}
                  onGespeichert={onGeaendert}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {neuOffen && canManage && (
        <NeueAdresse
          client={client}
          adressBezeichnungen={adressBezeichnungen}
          onFertig={async () => {
            setNeuOffen(false);
            await onGeaendert();
          }}
          onAbbrechen={() => setNeuOffen(false)}
        />
      )}
    </Abschnittskarte>
  );
}

/**
 * **PLZ zum Ort, Ort zur PLZ** (AP-31 Nachtrag) — Vorschläge im Dropdown,
 * übernommen nur auf Wahl. Automatisch eingetragen wird nichts: „9560" hatte
 * sonst still einen Ort gesetzt, der zur getippten Adresse nicht passte.
 */
function useOrtsvorschlaege() {
  const [vorschlaege, setVorschlaege] = useState<{ zip: Ortsvorschlag[]; city: Ortsvorschlag[] }>({ zip: [], city: [] });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Nur die Antwort auf die **letzte** Anfrage zählt — eine langsame ältere überschriebe sie sonst. */
  const letzteAnfrage = useRef(0);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const vorschlagen = (feld: 'zip' | 'city', text: string, land: string) => {
    if (timer.current) clearTimeout(timer.current);
    const mindestens = feld === 'zip' ? 3 : 2;
    if (text.trim().length < mindestens) {
      setVorschlaege((v) => ({ ...v, [feld]: [] }));
      return;
    }
    const nummer = ++letzteAnfrage.current;
    timer.current = setTimeout(async () => {
      try {
        const { data } = await clientsApi.places({ country: land, [feld]: text.trim() });
        if (nummer === letzteAnfrage.current) setVorschlaege((v) => ({ ...v, [feld]: data.data }));
      } catch {
        // Ein Vorschlag ist keine Bedingung — ohne ihn tippt man eben selbst.
        if (nummer === letzteAnfrage.current) setVorschlaege((v) => ({ ...v, [feld]: [] }));
      }
    }, 300);
  };

  return { vorschlaege, vorschlagen, leeren: () => setVorschlaege({ zip: [], city: [] }) };
}

/** Der Wert der Adressart-Auswahl aus Art und Bezeichnung. */
function artWertAus(type: AddressType, label: string | null | undefined, bezeichnungen: string[]) {
  if (type !== 'OTHER') return type;
  return label && bezeichnungen.includes(label) ? `${BEZEICHNUNG}${label}` : FREI;
}

/**
 * Eine Adresse als **Gruppe** (AP-36 Phase E): Art, Anschrift und „Standard"
 * gehören zusammen und gehen beim Verlassen des Eintrags in einem PATCH hinaus.
 * Die ID bleibt — ein Beleg, der auf die Adresse zeigt, zeigt danach noch auf sie.
 */
function AdressFelder({
  client,
  adresse,
  adressBezeichnungen,
  onGespeichert,
}: {
  client: ClientDetail;
  adresse: Address;
  adressBezeichnungen: string[];
  onGespeichert: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `address:${adresse.id}`,
    bezeichnung: `${adresse.street}, ${adresse.zip} ${adresse.city}`,
    daten: adresse as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const { data } = await api.patch<Datensatz>(
        `/clients/${client.id}/addresses/${adresse.id}`,
        patch,
        mitStand(stand),
      );
      // „Standard" nimmt die bisherige Standardadresse derselben Art mit.
      await onGespeichert();
      return data;
    },
    keepalive: { url: `/clients/${client.id}/addresses/${adresse.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  return (
    <Feldgruppe stelle={stelle} label={t('clients.sections.addresses')} className="mt-3 space-y-3 border-t border-border pt-3">
      <AdressEingaben stelle={stelle} adressBezeichnungen={adressBezeichnungen} />
    </Feldgruppe>
  );
}

function AdressEingaben({
  stelle,
  adressBezeichnungen,
}: {
  stelle: Speicherstelle<unknown>;
  adressBezeichnungen: string[];
}) {
  const { t } = useTranslation();
  const { vorschlaege, vorschlagen, leeren } = useOrtsvorschlaege();

  const art = useFeld<AddressType>(stelle, 'type', { art: 'auswahl', label: t('clients.addressType'), pflicht: true, lies: (x) => x as AddressType });
  const bezeichnung = useFeld(stelle, 'label', { label: t('clients.addressLabel') });
  const recipient = useFeld(stelle, 'recipient', { label: t('clients.recipient') });
  const addition = useFeld(stelle, 'addition', { label: t('clients.addition') });
  const street = useFeld(stelle, 'street', { label: t('clients.street'), pflicht: true });
  const zip = useFeld(stelle, 'zip', { label: t('clients.zip'), pflicht: true });
  const city = useFeld(stelle, 'city', { label: t('clients.city'), pflicht: true });
  const country = useFeld(stelle, 'country', { art: 'auswahl', label: t('clients.country'), pflicht: true });
  const standard = useFeld<boolean>(stelle, 'isDefault', { art: 'schalter', label: t('clients.setAsDefault') });

  const land = country.wert ?? 'AT';
  const artWert = artWertAus(art.wert ?? 'MAIN', bezeichnung.wert, adressBezeichnungen);

  /** Art und Bezeichnung hängen zusammen: Die Bezeichnung geht als Folgefeld mit. */
  const artWaehlen = (wert: string) => {
    if (wert === FREI) {
      art.setze('OTHER');
      return;
    }
    if (wert.startsWith(BEZEICHNUNG)) {
      bezeichnung.tippe(wert.slice(BEZEICHNUNG.length));
      art.setze('OTHER');
      return;
    }
    bezeichnung.tippe('');
    art.setze(wert as AddressType);
  };

  const uebernehmen = (v: Ortsvorschlag) => {
    zip.tippe(v.zip);
    city.setze(v.city);
    leeren();
  };

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Erste Zeile: nur die Adressart — die Anschrift beginnt darunter. */}
        <Feld label={t('clients.addressType')}>
          <select value={artWert} onChange={(e) => artWaehlen(e.target.value)} className={feldKlasse}>
            {FESTE_ADRESSARTEN.map((a) => (
              <option key={a} value={a}>
                {t(`clients.addressTypes.${a}`)}
              </option>
            ))}
            {adressBezeichnungen.map((b) => (
              <option key={b} value={`${BEZEICHNUNG}${b}`}>
                {b}
              </option>
            ))}
            <option value={FREI}>{t('clients.addressTypeFree')}</option>
          </select>
          <FeldStatus status={art} />
          {artWert === FREI && (
            <>
              <input
                type="text"
                maxLength={50}
                placeholder={t('clients.addressLabel')}
                className={`${feldKlasse} mt-2`}
                {...bezeichnung.props}
              />
              <FeldStatus status={bezeichnung} />
            </>
          )}
        </Feld>
        <div className="hidden sm:block" aria-hidden />

        <Feld label={t('clients.recipient')}>
          <input type="text" className={feldKlasse} {...recipient.props} />
          <FeldStatus status={recipient} />
        </Feld>
        <Feld label={t('clients.addition')}>
          <input type="text" className={feldKlasse} {...addition.props} />
          <FeldStatus status={addition} />
        </Feld>

        <div className="sm:col-span-2">
          <Feld label={t('clients.street')}>
            <input type="text" className={feldKlasse} {...street.props} />
            <FeldStatus status={street} />
          </Feld>
        </div>

        <div className="grid grid-cols-[7rem_1fr] gap-3 sm:col-span-2">
          <Feld label={t('clients.zip')}>
            <OrtVorschlagFeld
              inputMode="numeric"
              name={zip.props.name}
              value={zip.props.value}
              onChange={(wert) => {
                zip.tippe(wert);
                vorschlagen('zip', wert, land);
              }}
              onBlur={zip.props.onBlur}
              onKeyDown={zip.props.onKeyDown}
              vorschlaege={vorschlaege.zip}
              beschriftung={(v) => `${v.zip} ${v.city}`}
              schluessel={(v) => `${v.zip}|${v.city}`}
              onWaehlen={uebernehmen}
              className={feldKlasse}
            />
            <FeldStatus status={zip} />
          </Feld>
          <Feld label={t('clients.city')}>
            <OrtVorschlagFeld
              name={city.props.name}
              value={city.props.value}
              onChange={(wert) => {
                city.tippe(wert);
                vorschlagen('city', wert, land);
              }}
              onBlur={city.props.onBlur}
              onKeyDown={city.props.onKeyDown}
              vorschlaege={vorschlaege.city}
              beschriftung={(v) => `${v.city} (${v.zip})`}
              schluessel={(v) => `${v.zip}|${v.city}`}
              onWaehlen={uebernehmen}
              className={feldKlasse}
            />
            <FeldStatus status={city} />
          </Feld>
        </div>

        <Feld label={t('clients.country')}>
          {/* Auswahlfeld statt Freitext: In der Maske ist der ISO-Code hart. */}
          <select
            className={feldKlasse}
            {...country.props}
            onChange={(e) => {
              leeren();
              country.props.onChange(e);
            }}
          >
            {COUNTRIES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.de} ({l.code})
              </option>
            ))}
          </select>
          <FeldStatus status={country} />
        </Feld>
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" {...standard.schalter} />
        {t('clients.setAsDefault')}
        <FeldStatusZeichen status={standard} />
      </label>
      <FeldStatus status={standard} zeichen={false} />
    </>
  );
}

/** Neue Adresse: nur zum Anlegen, danach gilt Fall 1 am Eintrag. */
function NeueAdresse({
  client,
  adressBezeichnungen,
  onFertig,
  onAbbrechen,
}: {
  client: ClientDetail;
  adressBezeichnungen: string[];
  onFertig: () => Promise<void>;
  onAbbrechen: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateAddressPayload>(LEERE_ADRESSE);
  const [freieArt, setFreieArt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const { vorschlaege, vorschlagen, leeren } = useOrtsvorschlaege();

  const artWert = form.type !== 'OTHER' ? (form.type ?? 'MAIN') : freieArt ? FREI : artWertAus('OTHER', form.label, adressBezeichnungen);

  const artWaehlen = (wert: string) => {
    if (wert === FREI) {
      setFreieArt(true);
      setForm((f) => ({ ...f, type: 'OTHER', label: f.type === 'OTHER' ? f.label : '' }));
    } else if (wert.startsWith(BEZEICHNUNG)) {
      setFreieArt(false);
      setForm((f) => ({ ...f, type: 'OTHER', label: wert.slice(BEZEICHNUNG.length) }));
    } else {
      setFreieArt(false);
      setForm((f) => ({ ...f, type: wert as AddressType, label: '' }));
    }
  };

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await clientsApi.addAddress(client.id, form);
      await onFertig();
    } catch (err: unknown) {
      setFehler(fehlertext(err, t));
    }
  };

  return (
    <form onSubmit={anlegen} className="mt-4 space-y-3 border-t border-border pt-4">
      <p className="text-sm font-medium text-foreground">{t('clients.addAddress')}</p>
      <Fehlerzeile text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t('clients.addressType')}>
          <select value={artWert} onChange={(e) => artWaehlen(e.target.value)} className={eingabe}>
            {FESTE_ADRESSARTEN.map((a) => (
              <option key={a} value={a}>
                {t(`clients.addressTypes.${a}`)}
              </option>
            ))}
            {adressBezeichnungen.map((b) => (
              <option key={b} value={`${BEZEICHNUNG}${b}`}>
                {b}
              </option>
            ))}
            <option value={FREI}>{t('clients.addressTypeFree')}</option>
          </select>
          {artWert === FREI && (
            <input
              type="text"
              required
              maxLength={50}
              autoFocus={freieArt}
              placeholder={t('clients.addressLabel')}
              value={form.label ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className={`${eingabe} mt-2`}
            />
          )}
        </Feld>
        <div className="hidden sm:block" aria-hidden />

        <Feld label={t('clients.recipient')}>
          <input
            type="text"
            value={form.recipient ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, recipient: e.target.value }))}
            className={eingabe}
          />
        </Feld>
        <Feld label={t('clients.addition')}>
          <input
            type="text"
            value={form.addition ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, addition: e.target.value }))}
            className={eingabe}
          />
        </Feld>

        <div className="sm:col-span-2">
          <Feld label={t('clients.street')}>
            <input
              type="text"
              required
              value={form.street}
              onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
              className={eingabe}
            />
          </Feld>
        </div>

        <div className="grid grid-cols-[7rem_1fr] gap-3 sm:col-span-2">
          <Feld label={t('clients.zip')}>
            <OrtVorschlagFeld
              required
              inputMode="numeric"
              value={form.zip}
              onChange={(zip) => {
                setForm((f) => ({ ...f, zip }));
                vorschlagen('zip', zip, form.country);
              }}
              vorschlaege={vorschlaege.zip}
              beschriftung={(v) => `${v.zip} ${v.city}`}
              schluessel={(v) => `${v.zip}|${v.city}`}
              onWaehlen={(v) => {
                setForm((f) => ({ ...f, zip: v.zip, city: v.city }));
                leeren();
              }}
              className={eingabe}
            />
          </Feld>
          <Feld label={t('clients.city')}>
            <OrtVorschlagFeld
              required
              value={form.city}
              onChange={(city) => {
                setForm((f) => ({ ...f, city }));
                vorschlagen('city', city, form.country);
              }}
              vorschlaege={vorschlaege.city}
              beschriftung={(v) => `${v.city} (${v.zip})`}
              schluessel={(v) => `${v.zip}|${v.city}`}
              onWaehlen={(v) => {
                setForm((f) => ({ ...f, zip: v.zip, city: v.city }));
                leeren();
              }}
              className={eingabe}
            />
          </Feld>
        </div>

        <Feld label={t('clients.country')}>
          <select
            value={form.country}
            onChange={(e) => {
              leeren();
              setForm((f) => ({ ...f, country: e.target.value }));
            }}
            className={eingabe}
          >
            {COUNTRIES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.de} ({l.code})
              </option>
            ))}
          </select>
        </Feld>
      </div>
      <div className="flex justify-end gap-2">
        <AbbrechenKnopf onClick={onAbbrechen} />
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('common.create', { defaultValue: 'Anlegen' })}
        </button>
      </div>
    </form>
  );
}

// ─── Steuer & Zahlung ─────────────────────────────────────────────────────────

function ZahlungAbschnitt({
  client,
  stelle,
  canManage,
  istPerson,
}: {
  client: ClientDetail;
  stelle: Speicherstelle<unknown>;
  canManage: boolean;
  istPerson: boolean;
}) {
  const { t } = useTranslation();
  const gesperrt = !canManage;
  const vatId = useFeld(stelle, 'vatId', { label: t('clients.payment.vatId'), gesperrt });
  const taxNumber = useFeld(stelle, 'taxNumber', { label: t('clients.payment.taxNumber'), gesperrt });
  const taxScheme = useFeld(stelle, 'taxScheme', { art: 'auswahl', label: t('clients.payment.taxScheme'), pflicht: true, gesperrt });
  const currency = useFeld(stelle, 'currency', { art: 'auswahl', label: t('clients.payment.currency'), gesperrt });
  const language = useFeld(stelle, 'language', { art: 'auswahl', label: t('clients.payment.language'), gesperrt });

  return (
    <Abschnittskarte id="payment" titel={t('clients.sections.payment')}>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Feld label={t('clients.payment.vatId')}>
            <input type="text" className={feldKlasse} {...vatId.props} />
            <FeldStatus status={vatId} />
          </Feld>
          <Feld label={t('clients.payment.taxNumber')}>
            <input type="text" className={feldKlasse} {...taxNumber.props} />
            <FeldStatus status={taxNumber} />
          </Feld>
          {/* Bei einer Person gilt immer die Regelbesteuerung — das Feld wird
              gar nicht erst angeboten (AP-31 4.4). */}
          {!istPerson && (
            <Feld label={t('clients.payment.taxScheme')}>
              <select className={feldKlasse} {...taxScheme.props}>
                <option value="STANDARD">{t('clients.taxSchemes.STANDARD')}</option>
                <option value="REVERSE_CHARGE">{t('clients.taxSchemes.REVERSE_CHARGE')}</option>
                <option value="SMALL_BUSINESS">{t('clients.taxSchemes.SMALL_BUSINESS')}</option>
                <option value="EXEMPT">{t('clients.taxSchemes.EXEMPT')}</option>
              </select>
              <FeldStatus status={taxScheme} />
            </Feld>
          )}
        </div>

        {/* AP-31 Nachtrag: je Rolle eigene Bedingungen — ein Partner mit beiden
            Rollen zahlt anders, als er bezahlt werden will. */}
        {client.isCustomer && (
          <BedingungenFelder
            stelle={stelle}
            titel={t('clients.payment.asCustomer')}
            felder={{
              paymentTermsDays: 'paymentTermsDays',
              discountDays: 'discountDays',
              discountPercent: 'discountPercent',
              paymentMethod: 'paymentMethod',
            }}
            gesperrt={gesperrt}
          />
        )}
        {client.isSupplier && (
          <BedingungenFelder
            stelle={stelle}
            titel={t('clients.payment.asSupplier')}
            felder={{
              paymentTermsDays: 'supplierPaymentTermsDays',
              discountDays: 'supplierDiscountDays',
              discountPercent: 'supplierDiscountPercent',
              paymentMethod: 'supplierPaymentMethod',
            }}
            gesperrt={gesperrt}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t('clients.payment.currency')} hinweis={t('clients.payment.tenantDefault')}>
            <select className={feldKlasse} {...currency.props}>
              <option value="">—</option>
              {CURRENCIES.map((w) => (
                <option key={w.code} value={w.code}>
                  {w.code} — {w.de}
                </option>
              ))}
            </select>
            <FeldStatus status={currency} />
          </Feld>
          <Feld label={t('clients.payment.language')} hinweis={t('clients.payment.tenantDefault')}>
            <select className={feldKlasse} {...language.props}>
              <option value="">—</option>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
            <FeldStatus status={language} />
          </Feld>
        </div>
      </div>
    </Abschnittskarte>
  );
}

/**
 * Zahlungsziel, Skonto und Zahlungsart einer Rolle — eine **Gruppe**: Die
 * Skontofrist gilt nur vor dem Zahlungsziel, und der Server prüft beides am
 * zusammengeführten Stand.
 */
function BedingungenFelder({
  stelle,
  titel,
  felder,
  gesperrt,
}: {
  stelle: Speicherstelle<unknown>;
  titel: string;
  felder: { paymentTermsDays: string; discountDays: string; discountPercent: string; paymentMethod: string };
  gesperrt: boolean;
}) {
  const { t } = useTranslation();
  const zahl = (w: unknown) => (typeof w === 'number' ? w : null);

  return (
    <Feldgruppe
      stelle={stelle}
      label={titel}
      className="rounded-md border border-border px-3 pb-3 pt-2"
      pruefe={(w) => {
        const ziel = zahl(w[felder.paymentTermsDays]);
        const skonto = zahl(w[felder.discountDays]);
        return ziel !== null && skonto !== null && skonto >= ziel ? t('clients.discountBeforePaymentTerms') : null;
      }}
    >
      <p className="mb-2 px-1 text-sm font-semibold text-foreground">{titel}</p>
      <BedingungenEingaben stelle={stelle} felder={felder} gesperrt={gesperrt} />
    </Feldgruppe>
  );
}

function BedingungenEingaben({
  stelle,
  felder,
  gesperrt,
}: {
  stelle: Speicherstelle<unknown>;
  felder: { paymentTermsDays: string; discountDays: string; discountPercent: string; paymentMethod: string };
  gesperrt: boolean;
}) {
  const { t } = useTranslation();
  const tage = (w: number | null) => (w !== null && (!Number.isInteger(w) || w < 0 || w > 365) ? '0 bis 365 Tage' : null);
  const ziel = useFeld<number | null>(stelle, felder.paymentTermsDays, { art: 'zahl', label: t('clients.payment.paymentTermsDays'), gesperrt, pruefe: tage });
  const skontoTage = useFeld<number | null>(stelle, felder.discountDays, { art: 'zahl', label: t('clients.payment.discountDays'), gesperrt, pruefe: tage });
  const skonto = useFeld<number | null>(stelle, felder.discountPercent, {
    art: 'zahl',
    stellen: 2,
    label: t('clients.payment.discountPercent'),
    gesperrt,
    pruefe: (w) => (w !== null && (w < 0 || w > 100) ? '0 bis 100 %' : null),
  });
  const zahlungsart = useFeld(stelle, felder.paymentMethod, { art: 'auswahl', label: t('clients.payment.paymentMethod'), gesperrt });

  return (
    <div className="grid gap-4 sm:grid-cols-4">
      {[
        { f: ziel, label: t('clients.payment.paymentTermsDays') },
        { f: skontoTage, label: t('clients.payment.discountDays') },
        { f: skonto, label: t('clients.payment.discountPercent') },
      ].map(({ f, label }) => (
        <Feld key={f.props.name} label={label}>
          <input inputMode="decimal" className={feldKlasse} {...f.props} />
          <FeldStatus status={f} />
        </Feld>
      ))}
      <Feld label={t('clients.payment.paymentMethod')}>
        <select className={feldKlasse} {...zahlungsart.props}>
          <option value="">—</option>
          <option value="TRANSFER">{t('clients.paymentMethods.TRANSFER')}</option>
          <option value="DIRECT_DEBIT">{t('clients.paymentMethods.DIRECT_DEBIT')}</option>
          <option value="CASH">{t('clients.paymentMethods.CASH')}</option>
          <option value="CARD">{t('clients.paymentMethods.CARD')}</option>
        </select>
        <FeldStatus status={zahlungsart} />
      </Feld>
    </div>
  );
}

// ─── Buchhaltung ──────────────────────────────────────────────────────────────

function BuchhaltungAbschnitt({ stelle, canManage, lieferant }: { stelle: Speicherstelle<unknown>; canManage: boolean; lieferant: boolean }) {
  const { t } = useTranslation();
  const gesperrt = !canManage;
  // AP-34 7.8: Angaben für Eingangsrechnungen — was nicht gebucht ist, wird nicht angeboten.
  const eingang = useAuthStore((s) => s.hasModule)('INCOMING_INVOICES.CORE') && lieferant;
  const aufwandskonto = useFeld(stelle, 'defaultExpenseAccount', { label: t('clients.accounting.defaultExpenseAccount'), gesperrt });
  const subunternehmer = useFeld<boolean>(stelle, 'aghSubcontractor', { art: 'schalter', label: t('clients.accounting.aghSubcontractor'), gesperrt });
  const vertragsart = useFeld(stelle, 'aghContractType', { art: 'auswahl', label: t('clients.accounting.aghContractType'), gesperrt });
  const dienstgeber = useFeld(stelle, 'socialInsuranceEmployerNo', { label: t('clients.accounting.socialInsuranceEmployerNo'), gesperrt });
  const debitorNumber = useFeld(stelle, 'debitorNumber', { label: t('clients.accounting.debitorNumber'), gesperrt });
  const kreditorNumber = useFeld(stelle, 'kreditorNumber', { label: t('clients.accounting.kreditorNumber'), gesperrt });
  const iban = useFeld(stelle, 'iban', { label: t('clients.accounting.iban'), gesperrt });
  const bic = useFeld(stelle, 'bic', { label: t('clients.accounting.bic'), gesperrt });
  const accountHolder = useFeld(stelle, 'accountHolder', { label: t('clients.accounting.accountHolder'), gesperrt });
  const invoiceEmail = useFeld(stelle, 'invoiceEmail', { label: t('clients.accounting.invoiceEmail'), gesperrt });
  const externalId = useFeld(stelle, 'externalId', { label: t('clients.accounting.externalId'), gesperrt });
  const externalSource = useFeld(stelle, 'externalSource', { label: t('clients.accounting.externalSource'), gesperrt });

  const zeile = (f: ReturnType<typeof useFeld<string | null>>, label: string, art = 'text', hinweis?: string, platzhalter?: string) => (
    <Feld label={label} hinweis={hinweis}>
      <input type={art} placeholder={platzhalter} className={feldKlasse} {...f.props} />
      <FeldStatus status={f} />
    </Feld>
  );

  return (
    <Abschnittskarte id="accounting" titel={t('clients.sections.accounting')}>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {zeile(debitorNumber, t('clients.accounting.debitorNumber'))}
          {zeile(kreditorNumber, t('clients.accounting.kreditorNumber'))}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {zeile(iban, t('clients.accounting.iban'))}
          {zeile(bic, t('clients.accounting.bic'))}
          {zeile(accountHolder, t('clients.accounting.accountHolder'), 'text', t('clients.accounting.accountHolderHint'))}
        </div>

        {/* AP-31 Nachtrag: wohin Rechnungen als PDF per E-Mail gehen. */}
        {zeile(
          invoiceEmail,
          t('clients.accounting.invoiceEmail'),
          'email',
          t('clients.accounting.invoiceEmailHint'),
          'rechnung@beispiel.at',
        )}

        {eingang && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <p className="text-sm font-medium">{t('clients.accounting.incomingTitle')}</p>
            <div className="grid gap-4 sm:grid-cols-3">
              {zeile(aufwandskonto, t('clients.accounting.defaultExpenseAccount'), 'text', t('clients.accounting.defaultExpenseAccountHint'))}
              <Feld label={t('clients.accounting.aghSubcontractor')} hinweis={t('clients.accounting.aghSubcontractorHint')}>
                <label className="flex items-center gap-2 py-1.5 text-sm">
                  <input type="checkbox" {...subunternehmer.schalter} /> {t('clients.accounting.aghSubcontractorLabel')}
                </label>
              </Feld>
              {subunternehmer.wert && (
                <Feld label={t('clients.accounting.aghContractType')}>
                  <select className={feldKlasse} {...vertragsart.props}>
                    <option value="WORK">{t('clients.accounting.aghWork')}</option>
                    <option value="LABOUR_LEASING">{t('clients.accounting.aghLeasing')}</option>
                  </select>
                </Feld>
              )}
            </div>
            {subunternehmer.wert && <div className="grid gap-4 sm:grid-cols-3">{zeile(dienstgeber, t('clients.accounting.socialInsuranceEmployerNo'), 'text', t('clients.accounting.socialInsuranceEmployerNoHint'))}</div>}
          </div>
        )}

        {/* Die Fremdsystem-Zuordnung steht hier, hängt aber an keinem Feldrecht:
            Der Abgleich braucht sie (AP-09). Anzeigeort ist nicht Sperre. */}
        <div className="grid gap-4 sm:grid-cols-2">
          {zeile(externalId, t('clients.accounting.externalId'))}
          {zeile(externalSource, t('clients.accounting.externalSource'))}
        </div>
      </div>
    </Abschnittskarte>
  );
}

// ─── Aufträge ─────────────────────────────────────────────────────────────────

function AuftraegeAbschnitt({ client }: { client: ClientDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Abschnittskarte id="projects" titel={t('clients.sections.projects')}>
      {client.projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('clients.noProjects')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {client.projects.map((p) => (
            <li
              key={p.id}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="flex cursor-pointer items-center justify-between py-2 text-sm hover:bg-muted/30"
            >
              <span>
                <span className="font-mono text-xs text-muted-foreground">{p.projectNumber}</span>{' '}
                <span className="text-foreground">{p.name}</span>
              </span>
              <span className="text-xs text-muted-foreground">
                {p._count.timeEntries} {t('clients.timeEntries')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Abschnittskarte>
  );
}
